import type { AerialMode } from './aerialMotion'
import type { AttackKind, AttackPhase, Intent } from './simulation'

export type AttackTechnique='standard'|'dash-heavy-launcher'|'air-light'|'air-heavy'

export type AirCombatantState={
  aerialMode:AerialMode
  y:number
  vy:number
  juggleCount:number
}

export type AirCombatImpact={
  applied:boolean
  damageScale:number
  hitstunScale:number
  defenderVy:number
  defenderY:number
  defenderAerialMode:AerialMode
  juggleCount:number
  attackerLiftVelocity:number
}

const clamp=(value:number,min:number,max:number)=>Math.max(min,Math.min(max,value))
const airborne=(state:AirCombatantState)=>state.aerialMode!=='ground'||state.y>.001

export function resolveAttackTechnique(kind:AttackKind,attacker:AirCombatantState,intent:Intent):AttackTechnique{
  if(airborne(attacker)){
    if(kind==='light')return'air-light'
    if(kind==='heavy')return'air-heavy'
  }
  if(kind==='heavy'&&intent.dash)return'dash-heavy-launcher'
  return'standard'
}

export function resolveAirCombatImpact(
  technique:AttackTechnique,
  defender:AirCombatantState,
  blocked=false,
):AirCombatImpact{
  const neutral:AirCombatImpact={
    applied:false,
    damageScale:1,
    hitstunScale:1,
    defenderVy:defender.vy,
    defenderY:defender.y,
    defenderAerialMode:defender.aerialMode,
    juggleCount:defender.juggleCount,
    attackerLiftVelocity:0,
  }
  if(blocked)return neutral

  if(technique==='dash-heavy-launcher'&&!airborne(defender)){
    return{
      applied:true,
      damageScale:1.04,
      hitstunScale:1.06,
      defenderVy:6.25,
      defenderY:Math.max(.06,defender.y),
      defenderAerialMode:'ascent',
      juggleCount:1,
      attackerLiftVelocity:4.55,
    }
  }

  if(technique==='air-light'&&airborne(defender)){
    const count=Math.min(6,Math.max(0,Math.trunc(defender.juggleCount))+1)
    const exhausted=count>=5
    const defenderVy=exhausted?Math.min(defender.vy,-4.8):Math.max(defender.vy,Math.max(.8,2.7-(count-1)*.42))
    return{
      applied:true,
      damageScale:clamp(1-(count-1)*.1,.58,1),
      hitstunScale:clamp(1-(count-1)*.08,.62,1),
      defenderVy,
      defenderY:Math.max(.05,defender.y),
      defenderAerialMode:defenderVy>=0?'ascent':'descent',
      juggleCount:count,
      attackerLiftVelocity:0,
    }
  }

  if(technique==='air-heavy'&&airborne(defender)){
    const count=Math.min(6,Math.max(0,Math.trunc(defender.juggleCount))+1)
    return{
      applied:true,
      damageScale:clamp(1-(count-1)*.07,.68,1),
      hitstunScale:clamp(1-(count-1)*.05,.72,1),
      defenderVy:-8.8,
      defenderY:Math.max(.05,defender.y),
      defenderAerialMode:'descent',
      juggleCount:count,
      attackerLiftVelocity:0,
    }
  }

  return neutral
}

export function canAirCombatCancel(
  current:AttackTechnique,
  next:AttackKind,
  connected:boolean,
  phase:AttackPhase,
  attackerMode:AerialMode,
){
  if(!connected||phase!=='recovery'||attackerMode==='ground')return false
  if(current==='dash-heavy-launcher')return next==='light'||next==='heavy'
  if(current==='air-light')return next==='light'||next==='heavy'||next==='special'
  return false
}
